//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { skipRights } from '@apis/rights-management/rightsManagementApi';
import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { selectedItemActions } from '@uia/selected-item/selected-item-actions';

export interface SkipRightsMutationParams {
  cuid: PartialCuid;
  clientId: ClientId;
}

export const useSkipRightsMutation = ({
  onUpdateRights,
}: {
  onUpdateRights: () => void;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ clientId, cuid }: SkipRightsMutationParams) => {
      await skipRights({
        cuids: [cuid],
      });
      const metadata = await getMediaItemMetadata(
        Cuid.fromPartial(cuid, clientId),
      );
      return metadata;
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Skip Rights Failed',
          message: `Unable to skip rights. Please try again or contact Bazaarvoice Support.`,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSuccess: (metadata) => {
      dispatch(selectedItemActions.receiveItemMetadata(metadata));
      onUpdateRights();
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Skipped Rights',
          message: `Rights skipped`,
          type: ToastType.SUCCESS,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSettled: async (_, __, params) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsRights,
            QueryKeyConstants.UniversalImageActionsMetadata,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.MediaFilters,
            QueryKeyConstants.MediaFiltersMediaItems,
          ],
        }),
      ]);

      dispatch(selectedItemActions.reloadItemMetadata(params.cuid));
    },
  });
};
